import { Routes, Route, Navigate, useParams } from "react-router";
import LoginPage from "../pages/auth/LoginPage";
import DashboardLayout from "../layouts/DashboardLayout";
import DashboardHome from "../pages/dashboard/DashboardHome";
import CasesPage from "../pages/dashboard/CasesPage";
import RecoveryTargetsPage from "../pages/dashboard/RecoveryTargetsPage";
import SmsLogsPage from "../pages/dashboard/SmsLogsPage";
import CustomersPage from "../pages/dashboard/CustomersPage";
import ContactCentrePage from "../pages/dashboard/ContactCentrePage";
import FollowUpsPage from "../pages/dashboard/FollowUpsPage";
import LogContactPage from "../pages/dashboard/LogContactPage";
import EscalationsPage from "../pages/dashboard/EscalationsPage";
import InstitutionDetailPage from "../pages/dashboard/InstitutionDetailPage";
import InstitutionCustomersPage from "../pages/dashboard/InstitutionCustomersPage";
import InstitutionPortfolioGroupsPage from "../pages/dashboard/InstitutionPortfolioGroupsPage";
import InstitutionPortfolioGroupPage from "../pages/dashboard/InstitutionPortfolioGroupPage";
import OnboardInstitutionPage from "../pages/dashboard/OnboardInstitutionPage";
import PaymentsPage from "../pages/dashboard/PaymentsPage";
// Settlements hidden for now — re-enable when settlement workflow ships.
// import SettlementsPage from "../pages/dashboard/SettlementsPage";
// import RequestSettlementPage from "../pages/dashboard/RequestSettlementPage";
import TeamPage from "../pages/dashboard/TeamPage";
import RolesPage from "../pages/dashboard/RolesPage";
import InstitutionsPage from "../pages/dashboard/InstitutionsPage";
import SettingsPage from "../pages/dashboard/SettingsPage";
import AuditTrailPage from "../pages/dashboard/AuditTrailPage";
import ReportsPage from "../pages/dashboard/ReportsPage";
import ReportDetailPage from "../pages/dashboard/ReportDetailPage";
import UnauthorizedPage from "../pages/dashboard/UnauthorizedPage";
import ProtectedRoute, { GuestRoute, PermissionRoute } from "./ProtectedRoute";
import { OPS_PERMISSIONS as P } from "../config/permissions";

function ReportRoute() {
  const { slug } = useParams();
  return <ReportDetailPage key={slug} />;
}

function RedirectPortfolioUpload() {
  const { id, groupSlug } = useParams();
  return (
    <Navigate
      to={`/dashboard/institutions/${id}/portfolio-groups/${groupSlug}`}
      replace
    />
  );
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route
        path="/login"
        element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <PermissionRoute permission={P.DASHBOARD_VIEW}>
              <DashboardHome />
            </PermissionRoute>
          }
        />
        <Route path="assignment" element={<Navigate to="/dashboard/institutions" replace />} />
        <Route path="batches/:groupId" element={<Navigate to="/dashboard/institutions" replace />} />
        <Route path="batches" element={<Navigate to="/dashboard/institutions" replace />} />
        <Route
          path="cases"
          element={<Navigate to="/dashboard/cases/unassigned" replace />}
        />
        <Route
          path="cases/:queue"
          element={
            <PermissionRoute permissions={[P.CASES_VIEW, P.CASES_VIEW_ASSIGNED]}>
              <CasesPage />
            </PermissionRoute>
          }
        />
        <Route
          path="recovery-targets"
          element={
            <PermissionRoute permissions={[P.CASES_VIEW, P.CASES_VIEW_ASSIGNED]}>
              <RecoveryTargetsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="sms-logs"
          element={
            <PermissionRoute permissions={[P.CASES_VIEW, P.CASES_VIEW_ASSIGNED, P.AUDIT_VIEW]}>
              <SmsLogsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="customers"
          element={
            <PermissionRoute permission={P.CUSTOMERS_VIEW}>
              <CustomersPage />
            </PermissionRoute>
          }
        />
        <Route
          path="payments"
          element={
            <PermissionRoute permission={P.PAYMENTS_VIEW}>
              <PaymentsPage />
            </PermissionRoute>
          }
        />
        {/* Settlements hidden for now — re-enable when settlement workflow ships.
        <Route
          path="settlements"
          element={
            <PermissionRoute permission={P.SETTLEMENTS_VIEW}>
              <SettlementsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="settlements/request"
          element={
            <PermissionRoute permission={P.SETTLEMENTS_MANAGE}>
              <RequestSettlementPage />
            </PermissionRoute>
          }
        />
        */}
        <Route
          path="engagements"
          element={
            <PermissionRoute permission={P.CONTACT_CENTRE_VIEW}>
              <ContactCentrePage />
            </PermissionRoute>
          }
        />
        <Route
          path="follow-ups"
          element={
            <PermissionRoute permission={P.CONTACT_CENTRE_VIEW}>
              <FollowUpsPage />
            </PermissionRoute>
          }
        />
        <Route path="contact-centre" element={<Navigate to="/dashboard/engagements" replace />} />
        <Route path="communications" element={<Navigate to="/dashboard/engagements" replace />} />
        <Route
          path="reports"
          element={
            <PermissionRoute permissions={[P.REPORTS_VIEW, P.DASHBOARD_VIEW, P.CASES_VIEW, P.CASES_VIEW_ASSIGNED]}>
              <ReportsPage />
            </PermissionRoute>
          }
        />
        <Route path="sla" element={<Navigate to="/dashboard/reports/manager-sla-scorecard" replace />} />
        <Route
          path="reports/:slug"
          element={
            <PermissionRoute permissions={[P.REPORTS_VIEW, P.DASHBOARD_VIEW, P.CASES_VIEW, P.CASES_VIEW_ASSIGNED]}>
              <ReportRoute />
            </PermissionRoute>
          }
        />
        <Route
          path="log-contact"
          element={
            <PermissionRoute permission={P.CASES_LOG_INTERACTION}>
              <LogContactPage />
            </PermissionRoute>
          }
        />
        <Route path="escalate-case" element={<Navigate to="/dashboard/escalations" replace />} />
        <Route
          path="escalations"
          element={
            <PermissionRoute permission={P.ESCALATIONS_VIEW}>
              <EscalationsPage />
            </PermissionRoute>
          }
        />
        <Route path="legal" element={<Navigate to="/dashboard" replace />} />
        <Route path="legal/:id" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="team"
          element={
            <PermissionRoute permission={P.OPS_USERS_VIEW}>
              <TeamPage />
            </PermissionRoute>
          }
        />
        <Route
          path="roles"
          element={
            <PermissionRoute permission={P.OPS_USERS_MANAGE}>
              <RolesPage />
            </PermissionRoute>
          }
        />
        <Route
          path="institutions/onboard"
          element={
            <PermissionRoute permission={P.INSTITUTIONS_ONBOARD}>
              <OnboardInstitutionPage />
            </PermissionRoute>
          }
        />
        <Route
          path="institutions/:id/customers"
          element={
            <PermissionRoute permission={P.CUSTOMERS_VIEW}>
              <InstitutionCustomersPage />
            </PermissionRoute>
          }
        />
        <Route
          path="institutions/:id/portfolio-groups/:groupSlug/uploads/:uploadId"
          element={<RedirectPortfolioUpload />}
        />
        <Route
          path="institutions/:id/portfolio-groups/:groupSlug"
          element={
            <PermissionRoute permissions={[P.INSTITUTIONS_VIEW, P.CASES_VIEW, P.CASES_ASSIGN]}>
              <InstitutionPortfolioGroupPage />
            </PermissionRoute>
          }
        />
        <Route
          path="institutions/:id/portfolio-groups"
          element={
            <PermissionRoute permissions={[P.INSTITUTIONS_VIEW, P.CASES_VIEW, P.CASES_ASSIGN]}>
              <InstitutionPortfolioGroupsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="institutions/:id"
          element={
            <PermissionRoute permission={P.INSTITUTIONS_VIEW}>
              <InstitutionDetailPage />
            </PermissionRoute>
          }
        />
        <Route
          path="institutions"
          element={
            <PermissionRoute permission={P.INSTITUTIONS_VIEW}>
              <InstitutionsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="settings"
          element={
            <PermissionRoute permission={P.SETTINGS_VIEW}>
              <SettingsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="audit-trail"
          element={
            <PermissionRoute permission={P.AUDIT_VIEW}>
              <AuditTrailPage />
            </PermissionRoute>
          }
        />
        <Route path="activity" element={<Navigate to="/dashboard/audit-trail" replace />} />
        <Route path="unauthorized" element={<UnauthorizedPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
